import { createRoot } from 'react-dom/client';
import { CounterSale } from './pages/Counter';
import { ChecksPage } from './pages/Checks';
import { Component, useEffect, useState, type ReactNode } from 'react';
import { api, useLocation, match, navigate, resetPreviewPath, asset, DEMO, setCurrentMe, type Me } from './lib';
import { can } from '../shared/perms';
import { Icon, ToastProvider, Form, Field, Link, Loading, Modal, Select, useToast, PickSelect } from './ui';
import { useApi, type Row } from './lib';
import { Dashboard } from './pages/Dashboard';
import { WorkOrders, WorkOrderPage, EstimatePage, NewWorkOrder, openQuickJob, openEstimate } from './pages/WorkOrders';
import { ShopCopy } from './pages/ShopCopy';
import { Invoices, InvoicePage } from './pages/Invoices';
import { ReceiptPage } from './pages/Receipt';
import { Customers, CustomerPage, CustomerModal, UnitModal, UnitPage } from './pages/Customers';
import { Inventory } from './pages/Inventory';
import { LaborGuide } from './pages/LaborGuide';
import { Performance, PersonPerformance } from './pages/Performance';
import { Staff } from './pages/Staff';
import { Reports } from './pages/Reports';
import { Settings } from './pages/Settings';
import { ImportPage } from './pages/Import';
import { BayBoard } from './pages/Bays';
import { applyTheme, isLightTheme } from './theme';
import { Money } from './pages/Money';
import { MechanicApp } from './mechanic/MechanicApp';
import { PortalApp } from './portal/PortalApp';
import { PlatformApp } from './platform/PlatformApp';
import { ShopTv } from './ShopTv';
import { logoUrl } from './invoice-sheet';

function Root() {
  const { path } = useLocation();
  if (path.startsWith('/platform')) return <PlatformApp />;
  if (path.startsWith('/tv/')) return <ShopTv token={decodeURIComponent(path.slice(4))} />;
  return path.startsWith('/portal') ? <PortalApp /> : <App />;
}

function App() {
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [blocked, setBlocked] = useState('');
  const [shared, setShared] = useState(false);
  async function load() {
    try { setMe(await api<Me>('/api/me')); setShared(false); }
    catch (e) {
      // Work out which sign-in page this is BEFORE drawing it, so a shop's logo never flashes on the shared Asena Bay page.
      const st = (e as { status?: number }).status;
      if (st === 404 || st === 403) { setBlocked((e as Error).message); setMe(null); return; }
      // Shared sign-in address (app.asenabay.com) with no shop picked yet → Asena Bay sign-in page.
      let isShared = false, setup = false;
      try { isShared = !DEMO && !(await api<{ shop: unknown }>('/api/brand')).shop; } catch { /* older server */ }
      if (!isShared) { try { setup = (await api<{ needsSetup: boolean }>('/api/setup')).needsSetup; } catch { /* offline */ } }
      setShared(isShared); setNeedsSetup(setup); setMe(null);
    }
  }
  useEffect(() => {
    void load();
    const out = () => { setMe(undefined); void load(); };
    window.addEventListener('turan:signed-out', out);
    return () => window.removeEventListener('turan:signed-out', out);
  }, []);

  useEffect(() => { setCurrentMe(me || null); if (me) applyTheme(me.shop.theme); }, [me]);
  setCurrentMe(me || null);
  if (me === undefined) return <Loading />;
  if (!me && blocked) return <NoShop message={blocked} />;
  if (!me) return shared ? <Login shared onDone={load} /> : needsSetup ? <Setup onDone={load} /> : <Login onDone={load} />;
  const signOut = async () => { await api('/api/logout', { body: {} }).catch(() => {}); navigate('/'); setMe(undefined); void load(); };
  if (me.user.mustChange) return <ChoosePassword me={me} onDone={load} onSignOut={signOut} />;
  return <>
    {me.user.support && <div className={'support-bar ' + me.user.support}>
      <span><b>Asena Bay support</b> · {me.user.support === 'view' ? 'View only — nothing can be changed' : 'Editing — changes show as “Asena Bay support”'}</span>
      <button onClick={async () => { await api('/api/logout', { body: {} }).catch(() => {}); window.location.href = '/platform'; }}>Exit</button>
    </div>}
    <Shell me={me} onSignOut={signOut} />
  </>;
}

/** After a reset (or a new account) the password is temporary: they pick their own before anything else. Nobody else ever sees it. */
function ChoosePassword({ me, onDone, onSignOut }: { me: Me; onDone: () => void; onSignOut: () => void }) {
  return (
    <AuthCard shared title={`Hi ${me.user.name.split(' ')[0]}, choose your password`} sub="You signed in with a temporary password. Pick your own — only you will know it.">
      <Form submitLabel="Save and continue" onSubmit={async v => {
        if (v.password !== v.password2) throw new Error('Passwords do not match');
        await api('/api/me/password', { body: { password: v.password } }); onDone();
      }}>
        <Field label="New password" name="password" type="password" minLength={6} required full autoComplete="new-password" hint="At least 6 characters." />
        <Field label="Repeat new password" name="password2" type="password" minLength={6} required full autoComplete="new-password" />
      </Form>
      <p className="auth-note"><a href="#" onClick={e => { e.preventDefault(); onSignOut(); }}>Sign out</a></p>
    </AuthCard>
  );
}

function AuthCard({ title, sub, children, shared }: { title: string; sub: string; children: ReactNode; shared?: boolean }) {
  return (
    <div className="auth-split">
      <div className="auth-side">
        <div className="auth-inner">
          <img className={'auth-logo' + (shared ? ' asena-logo' : '')} src={shared ? asset('/brand/asena-logo.svg') : logoUrl()} alt={shared ? 'Asena Bay' : 'Shop logo'} />
          <h1>{title}</h1>
          <p>{sub}</p>
          {children}
        </div>
        {!shared && <div className="auth-foot"><img src={asset('/brand/asena-mark.svg')} alt="" /><span><b>Powered by Asena Bay</b><small>Run your truck shop like a pack.</small></span></div>}
      </div>
      <div className="auth-hero" style={{ backgroundImage: `url("${asset('/brand/hero.jpg')}"), radial-gradient(120% 90% at 70% 30%, #1f2a3a 0%, #0b1220 60%, #050a12 100%)` }} role="img" aria-label="Asena Bay — run your truck shop like a pack">
        <img className="hero-img" src={asset('/brand/hero.jpg')} alt="" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        <div className="auth-hero-text">
          <p className="hero-sub">Every bay. Every job. Every dollar.</p>
          <p>Work orders, parts, invoices and payroll for truck &amp; trailer shops — on the shop floor and in the office.</p>
        </div>
      </div>
    </div>
  );
}

function NoShop({ message }: { message: string }) {
  return (
    <AuthCard title="Nothing here yet" sub={message}>
      <p className="form-note">Running the platform? Go to <a href="/platform">/platform</a> to manage shops.</p>
    </AuthCard>
  );
}

function Login({ onDone, shared }: { onDone: () => void; shared?: boolean }) {
  return (
    <AuthCard shared={shared} title="Welcome back" sub="Sign in to your shop.">
      <Form submitLabel="Sign in" onSubmit={async v => { await api('/api/login', { body: v }); onDone(); }}>
        <Field label="Username" name="login" autoComplete="username" autoCapitalize="none" required full />
        <Field label="Password" name="password" type="password" autoComplete="current-password" required full />
      </Form>
      <p className="auth-note">Mechanics and office staff: use the username and password from your shop owner.</p>
    </AuthCard>
  );
}

function Setup({ onDone }: { onDone: () => void }) {
  return (
    <AuthCard title="Set up your shop" sub="Create the owner account. You'll need the setup code from your deployment (see README).">
      <Form submitLabel="Create owner account" onSubmit={async v => {
        if (v.password !== v.password2) throw new Error('Passwords do not match');
        await api('/api/setup', { body: v }); onDone();
      }}>
        <Field label="Setup code" name="setupCode" required full autoComplete="off" />
        <Field label="Your name" name="name" required full />
        <Field label="Username" name="login" required full autoCapitalize="none" />
        <Field label="Password" name="password" type="password" minLength={6} required full autoComplete="new-password" />
        <Field label="Repeat password" name="password2" type="password" minLength={6} required full autoComplete="new-password" />
      </Form>
    </AuthCard>
  );
}

const NAV = [
  { to: '/', label: 'Dashboard', icon: 'home', roles: ['owner', 'manager'] },
  { to: '/bays', label: 'Bay board', icon: 'board', roles: ['owner', 'manager'], bays: true },
  { to: '/work-orders', label: 'Work Orders', icon: 'clipboard', roles: ['owner', 'manager'] },
  { to: '/invoices', label: 'Invoices & Payments', icon: 'invoice', roles: ['owner', 'manager'] },
  { to: '/customers', label: 'Customers & Units', icon: 'truck', roles: ['owner', 'manager'] },
  { to: '/inventory', label: 'Inventory', icon: 'box', roles: ['owner', 'manager'] },
  { to: '/labor-guide', label: 'Labor guide', icon: 'wrench', roles: ['owner', 'manager'] },
  { to: '/staff', label: 'Staff & Payroll', icon: 'users', roles: ['owner', 'manager'] },
  { to: '/money', label: 'Money & Expenses', icon: 'dollar', roles: ['owner'], perm: 'money' as const },
  { to: '/performance', label: 'Team performance', icon: 'users', roles: ['owner'], perm: 'reports' as const },
  { to: '/reports', label: 'Reports', icon: 'chart', roles: ['owner'], perm: 'reports' as const },
  { to: '/settings', label: 'Settings', icon: 'gear', roles: ['owner'] },
];

function Shell({ me, onSignOut }: { me: Me; onSignOut: () => void }) {
  const { path } = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [path]);

  // Mechanics — and anyone on /floor — get the phone-first shop floor app.
  if (me.user.role === 'mechanic' || path.startsWith('/floor')) return <MechanicApp me={me} onSignOut={onSignOut} />;

  const nav = NAV.filter(n => (n.roles.includes(me.user.role) || ('perm' in n && n.perm && can(me.user, n.perm))) && (!('bays' in n) || me.shop.bays_on === '1'));
  let page: ReactNode = <NotFound />;
  let m;
  if (path === '/') page = <Dashboard me={me} />;
  else if (path === '/work-orders') page = <WorkOrders me={me} />;
  else if (path === '/bays') page = <BayBoard />;
  else if (path === '/checks') page = <ChecksPage />;
  else if ((m = match('/work-orders/:id/estimate', path))) page = <EstimatePage id={m.id} />;
  else if ((m = match('/work-orders/:id/shop-copy', path))) page = <ShopCopy id={m.id} />;
  else if ((m = match('/work-orders/:id', path))) page = <WorkOrderPage id={m.id} me={me} />;
  else if (path === '/invoices') page = <Invoices />;
  else if ((m = match('/invoices/:id', path))) page = <InvoicePage id={m.id} me={me} />;
  else if ((m = match('/receipts/:no', path))) page = <ReceiptPage no={decodeURIComponent(m.no)} />;
  else if (path === '/customers') page = <Customers />;
  else if ((m = match('/customers/:id', path))) page = <CustomerPage id={m.id} />;
  else if ((m = match('/units/:id', path))) page = <UnitPage id={m.id} />;
  else if (path === '/inventory') page = <Inventory me={me} />;
  else if (path === '/labor-guide') page = <LaborGuide />;
  else if (path === '/performance') page = <Performance />;
  else if ((m = match('/performance/:id', path))) page = <PersonPerformance id={m.id} />;
  else if (path === '/staff') page = <Staff me={me} />;
  else if (path === '/reports' && can(me.user, 'reports')) page = <Reports />;
  else if (path === '/money' && can(me.user, 'money')) page = <Money />;
  else if (path === '/settings' && me.user.role === 'owner') page = <Settings me={me} />;
  else if (path === '/import' && me.user.role === 'owner') page = <ImportPage />;

  return (
    <div className={'shell' + (open ? ' nav-open' : '')}>
      <aside className="sidebar">
        <div className="brand"><img src={logoUrl(me.shop.logo_version) + (DEMO || isLightTheme(me.shop.theme) ? '' : '&dark=1')} alt={me.shop.shop_name} /></div>
        <CreateMenu me={me} onPicked={() => setOpen(false)} />
        <nav>
          {nav.map(n => {
            const active = n.to === '/' ? path === '/' : path.startsWith(n.to);
            return <Link key={n.to} to={n.to} className={active ? 'active' : ''}><Icon name={n.icon} />{n.label}</Link>;
          })}
          <Link to="/floor" className=""><Icon name="phone" />Shop floor view</Link>
        </nav>
        <div className="sidebar-foot">
          <div className="who"><b>{me.user.name}</b><small>{me.user.role === 'owner' ? 'Owner' : 'Manager'} · {me.platform || 'Asena Bay'}</small></div>
          <button className="icon-btn light" onClick={onSignOut} title="Sign out" aria-label="Sign out"><Icon name="logout" /></button>
        </div>
      </aside>
      <div className="scrim" onClick={() => setOpen(false)} />
      <div className="main">
        <header className="topbar">
          <button className="icon-btn menu-btn" onClick={() => setOpen(!open)} aria-label="Menu"><Icon name="menu" /></button>
          <div className="topbar-title">{me.shop.shop_name}</div>
          <CreateMenu me={me} compact onPicked={() => setOpen(false)} />
        </header>
        <main className="content">{page}</main>
      </div>
    </div>
  );
}

/** "+ Create": start anything from anywhere — work order, quick job, customer, part, expense, receipt, staff… */
function CreateMenu({ me, compact, onPicked }: { me: Me; compact?: boolean; onPicked: () => void }) {
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState<null | 'wo' | 'quick' | 'customer' | 'vehicle' | 'counter'>(null);
  const owner = me.user.role === 'owner';
  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k);
  }, [open]);
  const go = (to: string) => { setOpen(false); onPicked(); navigate(to); };
  const pop = (m: 'wo' | 'quick' | 'customer' | 'vehicle' | 'counter') => { setOpen(false); onPicked(); setModal(m); };
  const groups: [string, [string, () => void, boolean?][]][] = [
    ['Work', [['Work order', () => pop('wo')], ['Quick job', () => { setOpen(false); onPicked(); void openQuickJob(); }], ['Estimate', () => { setOpen(false); onPicked(); void openEstimate('shop'); }], ['Road service estimate', () => { setOpen(false); onPicked(); void openEstimate('road'); }], ['Counter sale (parts)', () => pop('counter')], ['Receive payment', () => go('/invoices')]]],
    ['Customers', [['Customer', () => pop('customer')], ['Truck / trailer', () => pop('vehicle')]]],
    ['Parts', [['Part', () => go('/inventory?new=1')], ['Parts order', () => go('/inventory?tab=orders&new=1')], ['Vendor', () => go('/inventory?tab=vendors&new=1')], ['Supplier bill', () => go('/money?tab=suppliers'), true]]],
    ['Money', [['Expense', () => go('/money?tab=expenses&new=1'), true], ['Store receipt', () => go('/money?tab=purchases&new=1'), true], ['Regular bill', () => go('/money?tab=bills'), true]]],
    ['Team', [['Staff member', () => go('/staff?new=1'), true], ['Time entry', () => go('/staff?tab=time'), true]]],
  ];
  return (
    <div className={'create-wrap' + (compact ? ' compact' : '')}>
      <button type="button" className={'create-btn' + (open ? ' open' : '')} aria-expanded={open} onClick={() => setOpen(!open)}>
        <Icon name={open ? 'x' : 'plus'} size={18} />{!compact && <span>Create</span>}
      </button>
      {open && <>
        <div className="create-scrim" onClick={() => setOpen(false)} />
        <div className="create-pop" role="menu">
          <h3>Create</h3>
          <div className="create-grid">
            {groups.map(([title, items]) => {
              const shown = items.filter(([, , ownerOnly]) => !ownerOnly || owner);
              return shown.length ? (
                <div key={title}><h4>{title}</h4>{shown.map(([label, fn]) => <button key={label} type="button" role="menuitem" onClick={fn}>{label}</button>)}</div>
              ) : null;
            })}
          </div>
        </div>
      </>}
      {(modal === 'wo' || modal === 'quick') && <NewWorkOrder me={me} initialKind={modal === 'quick' ? 'quick' : 'standard'} onClose={() => setModal(null)} />}
      {modal === 'vehicle' && <VehicleForCustomer onClose={() => setModal(null)} />}
      {modal === 'counter' && <CounterSale onClose={() => setModal(null)} />}
      {modal === 'customer' && <CustomerModal onClose={() => setModal(null)} onSaved={id => { setModal(null); navigate('/customers/' + id); }} />}
    </div>
  );
}

/** + Create → Truck / trailer: pick whose it is, then the vehicle form. */
function VehicleForCustomer({ onClose }: { onClose: () => void }) {
  const customers = useApi<Row[]>('/api/customers');
  const [cust, setCust] = useState('');
  const toast = useToast();
  if (cust) return <UnitModal customerId={cust} onClose={onClose} onSaved={() => { toast('Vehicle saved'); onClose(); navigate('/customers/' + cust); }} />;
  return (
    <Modal title="Add vehicle — whose is it?" onClose={onClose}>
      <div className="form"><div className="form-body">
        <label className="field full"><span>Customer</span>
          <PickSelect value={cust} ariaLabel="Customer" placeholder="Select customer…" options={(customers.data || []).map(c => ({ value: c.id, label: c.name }))} onChange={setCust} /></label>
      </div></div>
    </Modal>
  );
}

const NotFound = () => <div className="empty">Page not found. <Link to="/">Go to dashboard</Link></div>;

/** A crash on one screen shows what broke and a way back — never a blank white page. */
class Crash extends Component<{ children: ReactNode }, { err: string }> {
  declare props: { children: ReactNode };
  state = { err: '' };
  declare setState: (s: { err: string }) => void;
  static getDerivedStateFromError(e: unknown) { return { err: e instanceof Error ? e.message : String(e) }; }
  componentDidCatch(e: unknown) { console.error(e); }
  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div className="crash">
        <h2>Something on this screen broke</h2>
        <p className="muted">Nothing was lost. Go back, or reload the page.</p>
        <code>{this.state.err}</code>
        <div className="actions-row">
          <button className="btn secondary" onClick={() => { this.setState({ err: '' }); history.back(); }}>Go back</button>
          <button className="btn primary" onClick={() => location.reload()}>Reload</button>
        </div>
        <p className="muted small">If it keeps happening, send a photo of this screen to support.</p>
      </div>
    );
  }
}

const rootEl = document.getElementById('root')!;
let root = createRoot(rootEl);
root.render(<Crash><ToastProvider><Root /></ToastProvider></Crash>);
if (DEMO) {
  // Preview page: start over as a different person (owner / mechanic / customer) without reloading.
  (DEMO as unknown as { remount: (start: string) => void }).remount = start => {
    root.unmount();
    resetPreviewPath(start);
    root = createRoot(rootEl);
    root.render(<Crash><ToastProvider><Root /></ToastProvider></Crash>);
  };
}

if ('serviceWorker' in navigator && location.protocol === 'https:' && !(window as unknown as { __TURAN_DEMO__?: unknown }).__TURAN_DEMO__) {
  navigator.serviceWorker.register('/sw.js').catch(() => {});
}
